import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, setStudentId, goToGoogleLogin } from "@/lib/api";

const STANDARDS = [5, 6, 7, 8, 9];
const BOARDS = ["CBSE"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [standard, setStandard] = useState("6");
  const [board, setBoard] = useState("CBSE");
  const [language, setLanguage] = useState("English");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please tell us your name first!");
      return;
    }
    if (!email.trim() || password.length < 6) {
      toast.error("Please enter your email and a password with at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", {
        name: name.trim(), standard: parseInt(standard, 10), board, preferred_language: language, country: "India",
        email: email.trim(), password,
      });
      setStudentId(data.id);
      toast.success(`Welcome aboard, ${data.name}!`);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    if (!name.trim()) {
      toast.error("Please tell us your name first!");
      return;
    }
    goToGoogleLogin({ standard: parseInt(standard, 10), board });
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary/20 rounded-full blur-3xl" />
      <div className="relative z-10 w-full max-w-md bg-card rounded-3xl shadow-2xl p-6 sm:p-8 animate-float-in border border-border">
        <div className="flex items-center gap-3 mb-2">
          <img
            src="https://customer-assets-jai6qajn.emergentagent.net/job_student-genius-26/artifacts/kbshcq5q_Branding-02.png"
            alt="SmartGuru Logo Icon"
            className="w-11 h-11 object-contain"
          />
          <h1 className="text-2xl font-heading font-extrabold bg-gradient-to-r from-[#9A3DB2] via-[#4F4CB9] to-[#E73F9A] bg-clip-text text-transparent">SmartGuru.io</h1>
        </div>
        <p className="text-muted-foreground text-sm mb-6 flex items-center gap-1">
          <Sparkles size={14} className="text-secondary" /> Your personal AI teacher, just for you.
        </p>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="onboarding-form">
          <div>
            <Label htmlFor="name">What's your name?</Label>
            <Input id="name" data-testid="onboarding-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Aarav" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Your Class</Label>
              <Select value={standard} onValueChange={setStandard}>
                <SelectTrigger data-testid="onboarding-standard-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STANDARDS.map((s) => (
                    <SelectItem key={s} value={String(s)} data-testid={`standard-option-${s}`}>Class {s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Board</Label>
              <Select value={board} onValueChange={setBoard}>
                <SelectTrigger data-testid="onboarding-board-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOARDS.map((b) => (
                    <SelectItem key={b} value={b} data-testid={`board-option-${b}`}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">Class 5 to Class 9 CBSE Math & Science are fully ready today!</p>
          <div>
            <Label>Preferred Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="onboarding-language-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Hindi">Hindi (coming soon)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="onboarding-email">Email</Label>
            <Input id="onboarding-email" type="email" data-testid="onboarding-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@gmail.com" className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="onboarding-password">Password</Label>
            <Input id="onboarding-password" type="password" data-testid="onboarding-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters" className="mt-1.5" />
          </div>
          <Button type="submit" data-testid="onboarding-submit-button" disabled={loading} className="w-full h-12 rounded-xl text-base font-semibold">
            {loading ? "Setting things up..." : "Create Account & Start Learning"}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <button
          type="button"
          onClick={handleGoogleSignup}
          data-testid="onboarding-google-button"
          className="w-full h-12 rounded-xl border border-border bg-white text-[#1F1F1F] font-semibold flex items-center justify-center gap-2.5 hover:bg-white/90 active:scale-95 transition-all"
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z" />
            <path fill="#4CAF50" d="M24 44c5.3 0 10.1-1.8 13.9-5l-6.4-5.4C29.5 35.4 26.9 36 24 36c-5.3 0-9.9-3.4-11.5-8.1l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.7-6.4 7.3l6.4 5.4C39.4 37.2 44 31.5 44 24c0-1.3-.1-2.7-.4-3.5z" />
          </svg>
          Continue with Google
        </button>
        <p className="text-sm text-muted-foreground text-center mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="onboarding-login-link" className="text-primary font-semibold hover:underline">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}

